import JSZip from "jszip";

/**
 * Extrait et importe un fichier ZIP de variante/référentiel en téléchargeant chaque fichier sur le serveur.
 * 
 * @param {File} file
 * @returns {Promise<{
 *   successCount: number,
 *   errorCount: number,
 *   originalVariantName: string,
 *   targetChapterName: string,
 *   targetFolderName: string
 * }>}
 */
export const importZipVariant = async (file) => {
  // Récupérer le nom du dossier depuis le nom du fichier ZIP
  const sourceFolderName = file.name
    .replace(/\.zip$/i, '')
    .replace(/\s*\(\d+\)\s*$/, '');
  const normalizedSourceFolderName = sourceFolderName.replace(/_export$/i, '');

  const zip = new JSZip();
  const zipContent = await zip.loadAsync(file);
  
  const targetChapterName = normalizedSourceFolderName.replace(/_[A-Za-z0-9-]+$/, '') || normalizedSourceFolderName;

  const files = [];
  zipContent.forEach((relativePath, zipEntry) => {
    if (!zipEntry.dir) {
      files.push({ path: relativePath, entry: zipEntry });
    }
  });

  if (files.length === 0) {
    throw new Error('Le ZIP ne contient aucun fichier.');
  }

  // Détecter le nom d'origine des fichiers JSON dans le ZIP
  let detectedSourceFolderName = '';
  for (const f of files) {
    const filename = f.path.split('/').pop();
    if (filename.endsWith('.json') && !filename.endsWith('_learn.json') && !filename.startsWith('._')) {
      detectedSourceFolderName = filename.replace(/\.json$/i, '');
      break;
    }
  }
  if (!detectedSourceFolderName) {
    for (const f of files) {
      const filename = f.path.split('/').pop();
      if (filename.endsWith('.json') && !filename.startsWith('._')) {
        detectedSourceFolderName = filename.replace(/(_learn)?\.json$/i, '');
        break;
      }
    }
  }
  const originalVariantName = detectedSourceFolderName || normalizedSourceFolderName;

  // Le nom de la variante cible vient du JSON trouvé DANS le zip, pas du nom du fichier zip
  // (qui peut être renommé/tronqué par l'OS, ou être celui d'un autre dossier si le mauvais
  // zip a été sélectionné).
  const targetFolderName = originalVariantName;

  // Détecter et ignorer le wrapper du dossier créé par Windows/macOS
  let commonPrefix = '';
  if (files.length > 0) {
    const firstPath = files[0].path;
    const firstParts = firstPath.split('/').filter(Boolean);
    if (firstParts.length > 0) {
      const potentialPrefix = firstParts[0];
      const allStartWithPrefix = files.every(f => f.path.startsWith(potentialPrefix + '/'));
      if (allStartWithPrefix) {
        commonPrefix = potentialPrefix + '/';
      }
    }
  }

  let successCount = 0;
  let errorCount = 0;

  for (let i = 0; i < files.length; i++) {
    const { path: relativePath, entry } = files[i];
    try {
      const blob = await entry.async('blob');
      let cleanPath = relativePath;
      if (commonPrefix && relativePath.startsWith(commonPrefix)) {
        cleanPath = relativePath.slice(commonPrefix.length);
      }
      const pathParts = cleanPath.split('/').filter(Boolean);
      const filename = pathParts[pathParts.length - 1];

      let finalFilename = filename;
      if (filename.endsWith('.bytes')) {
        finalFilename = filename.replace('.bytes', '.glb');
      }
      const isLearnFile = pathParts.includes('learn');
      const isMainProcessJson = !isLearnFile && finalFilename === `${originalVariantName}.json`;

      if (isLearnFile && filename.toLowerCase().endsWith('.json')) {
        finalFilename = `${targetFolderName}_learn.json`;
      } else if (isMainProcessJson) {
        finalFilename = `${targetFolderName}.json`;
      } else if (finalFilename === `${originalVariantName}_learn.json`) {
        finalFilename = `${targetFolderName}_learn.json`;
      } else if (finalFilename.startsWith(`${originalVariantName}_`)) {
        finalFilename = `${targetFolderName}${finalFilename.slice(originalVariantName.length)}`;
      }

      // Le fichier JSON principal est renommé pour matcher targetFolderName, mais son contenu
      // garde le titre d'origine (process.processTitle) si on ne le corrige pas ici.
      let uploadBlob = blob;
      if (isMainProcessJson && targetFolderName !== originalVariantName) {
        try {
          const jsonData = JSON.parse(await blob.text());
          if (jsonData.process) {
            jsonData.process.processTitle = targetFolderName;
          }
          uploadBlob = new Blob([JSON.stringify(jsonData, null, 2)], { type: 'application/json' });
        } catch (parseErr) {
          console.warn(`Impossible de mettre à jour processTitle pour ${finalFilename} :`, parseErr.message);
        }
      }

      const formData = new FormData();
      formData.append('file', uploadBlob, finalFilename);
      // Cet import cible directement targetFolderName (une seule variante) : le seul
      // sous-dossier légitime est "learn/". Un autre segment résiduel (wrapper du zip non
      // détecté comme commonPrefix) ne doit pas être conservé comme subPath, sinon il se
      // retrouve dupliqué sous targetFolderName.
      let subPath = '';
      if (finalFilename.endsWith('_learn.json') || isLearnFile) {
        subPath = 'learn';
      }
      if (subPath) formData.append('subPath', subPath);

      const response = await fetch(
        `/api/data/${encodeURIComponent(targetChapterName)}/${encodeURIComponent(targetFolderName)}/upload-single`,
        { method: 'POST', body: formData, credentials: 'include' }
      );
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || `Status ${response.status}`);
      }
      successCount++;
    } catch (err) {
      errorCount++;
      console.error(`Erreur pour ${relativePath}:`, err.message);
    }
  }

  return {
    successCount,
    errorCount,
    originalVariantName,
    targetChapterName,
    targetFolderName
  };
};

/**
 * Importe les fichiers d'une variante déjà organisés en dossier (utilisé en interne
 * par importZip lorsqu'un zip de référentiel complet contient plusieurs variantes).
 *
 * @param {string} chapterName - Nom du référentiel cible
 * @param {string} variantName - Nom de la variante cible (dossier de langue)
 * @param {{entry: import('jszip').JSZipObject, rest: string[]}[]} variantFiles
 */
const uploadVariantFiles = async (chapterName, variantName, variantFiles) => {
  let successCount = 0;
  let errorCount = 0;

  for (const { entry, rest } of variantFiles) {
    try {
      const blob = await entry.async('blob');
      const filename = rest[rest.length - 1];
      const subPath = rest.slice(0, -1).join('/');

      const formData = new FormData();
      formData.append('file', blob, filename);
      if (subPath) formData.append('subPath', subPath);

      const response = await fetch(
        `/api/data/${encodeURIComponent(chapterName)}/${encodeURIComponent(variantName)}/upload-single`,
        { method: 'POST', body: formData, credentials: 'include' }
      );
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || `Status ${response.status}`);
      }
      successCount++;
    } catch (err) {
      errorCount++;
      console.error(`Erreur pour ${variantName}/${rest.join('/')}:`, err.message);
    }
  }

  return { successCount, errorCount };
};

/**
 * Importe un zip de référentiel complet (plusieurs variantes/langues à l'intérieur).
 *
 * @param {string} chapterName
 * @param {{entry: import('jszip').JSZipObject, parts: string[]}[]} cleanedFiles
 */
const importFullReferential = async (chapterName, cleanedFiles) => {
  const byVariant = new Map();
  for (const { entry, parts } of cleanedFiles) {
    const [variantName, ...rest] = parts;
    if (!byVariant.has(variantName)) byVariant.set(variantName, []);
    byVariant.get(variantName).push({ entry, rest });
  }

  let successCount = 0;
  let errorCount = 0;

  for (const [variantName, variantFiles] of byVariant) {
    const result = await uploadVariantFiles(chapterName, variantName, variantFiles);
    successCount += result.successCount;
    errorCount += result.errorCount;
  }

  return {
    successCount,
    errorCount,
    targetChapterName: chapterName,
    variantCount: byVariant.size
  };
};

/**
 * Analyse la structure d'un ZIP (sans rien envoyer au serveur) pour déterminer
 * s'il s'agit d'un référentiel complet (plusieurs variantes) ou d'une seule variante.
 */
const detectZipStructure = async (file) => {
  const zip = new JSZip();
  const zipContent = await zip.loadAsync(file);

  const rawFiles = [];
  zipContent.forEach((relativePath, zipEntry) => {
    if (!zipEntry.dir) rawFiles.push({ path: relativePath, entry: zipEntry });
  });

  if (rawFiles.length === 0) {
    throw new Error('Le ZIP ne contient aucun fichier.');
  }

  // Détecter et retirer un éventuel dossier wrapper commun à tous les fichiers
  let commonPrefix = '';
  const firstParts = rawFiles[0].path.split('/').filter(Boolean);
  if (firstParts.length > 0) {
    const potentialPrefix = firstParts[0];
    if (rawFiles.every(f => f.path.startsWith(potentialPrefix + '/'))) {
      commonPrefix = potentialPrefix + '/';
    }
  }

  const cleanedFiles = rawFiles.map(f => ({
    entry: f.entry,
    parts: (commonPrefix && f.path.startsWith(commonPrefix) ? f.path.slice(commonPrefix.length) : f.path)
      .split('/')
      .filter(Boolean)
  }));

  // Dossiers de premier niveau (hors "learn") = candidats "dossiers de variante"
  const topLevelFolders = new Set(
    cleanedFiles
      .filter(f => f.parts.length > 1 && f.parts[0].toLowerCase() !== 'learn')
      .map(f => f.parts[0])
  );

  // Un référentiel complet = un dossier wrapper (le nom du référentiel) contenant un ou
  // plusieurs dossiers de variante. Les noms de variante sont du texte libre (pas de lien
  // de nommage avec le référentiel, ex: "Variante 2 assemblage" sous "Référentiel") : on ne
  // peut donc pas exiger qu'ils suivent une convention "<référentiel>_xxx" comme avant — la
  // seule présence d'un sous-dossier (hors "learn") après le wrapper suffit à distinguer un
  // référentiel (2 niveaux : wrapper/variante/fichier) d'un zip de variante seule (1 niveau :
  // variante/fichier). Ça reste vrai même avec une seule variante à l'intérieur.
  const chapterCandidate = commonPrefix ? commonPrefix.replace(/\/$/, '') : null;

  const isReferential = topLevelFolders.size > 0 && !!chapterCandidate;

  const chapterName = isReferential ? chapterCandidate : null;

  return { cleanedFiles, isReferential, chapterName };
};

/**
 * Détecte, sans rien importer, le type de ZIP et le nom de référentiel visé
 * (utile pour vérifier en amont un conflit de nom et proposer un renommage).
 *
 * @param {File} file
 * @returns {Promise<{type: 'variant'} | {type: 'referential', chapterName: string}>}
 */
export const peekZipTarget = async (file) => {
  const { isReferential, chapterName } = await detectZipStructure(file);
  return isReferential ? { type: 'referential', chapterName } : { type: 'variant' };
};

/**
 * Importe un fichier ZIP en détectant automatiquement s'il s'agit :
 * - d'une seule variante (fichiers à plat, éventuellement dans un dossier "learn")
 * - ou d'un référentiel complet (plusieurs dossiers de variantes à l'intérieur)
 *
 * @param {File} file
 * @param {{ chapterNameOverride?: string }} [options] - Permet d'imposer le nom du
 *   référentiel cible pour un import de type "referential" (ex: pour éviter un conflit de nom).
 * @returns {Promise<{type: 'variant'|'referential'} & Record<string, any>>}
 */
export const importZip = async (file, options = {}) => {
  const { chapterNameOverride } = options;
  const { cleanedFiles, isReferential, chapterName } = await detectZipStructure(file);

  if (isReferential) {
    const targetChapterName = chapterNameOverride || chapterName;
    const result = await importFullReferential(targetChapterName, cleanedFiles);
    return { type: 'referential', ...result };
  }

  const singleResult = await importZipVariant(file);
  return { type: 'variant', ...singleResult };
};
